// Utility functions for clearing localStorage statistics when users log in

import SharedConfig from '../shared/config/gameConfig.data.js';
const { UNIFIED_GAMES_CONFIG_DATA } = SharedConfig;

// Keys that should never be removed (exclusion list for safety)
const PRESERVED_KEYS = [
  'authToken',
  'userPreferences', 
  'userSettings',
  'temporaryGameData',
  'notification-settings',
  'appPreferences',
  'theme',
  'volume'
];

/**
 * Dynamically generate game-related localStorage keys from the SSoT.
 * @returns {Set<string>} A Set of all known game-related localStorage keys.
 */
const getDynamicGameKeys = () => {
  const keys = new Set();
  
  UNIFIED_GAMES_CONFIG_DATA.forEach(game => {
    // Add all known keys from the config
    keys.add(game.streakKey);
    keys.add(game.dailyKey);
    if (game.sessionStorageKey) {
      keys.add(game.sessionStorageKey);
    }
    
    // Add derived keys (e.g., for best streak history - common pattern in apps)
    keys.add(`${game.streakKey}_best`);
    
    // Add legacy variations that might exist in localStorage
    keys.add(`${game.streakKey}Best`);
    keys.add(`${game.dailyKey}Game`);
    
    // Add legacy daily game keys that tests expect
    if (game.id === 'slay-the-spire') {
      keys.add('stsDailyGame'); // Legacy key for Slay the Spire daily games
    }
    if (game.id === 'bible-books') {
      keys.add('bibleBooksDaily'); // Legacy key for Bible Books daily games
    }
    if (game.id === 'subnautica-creatures') {
      keys.add('subnauticaCreaturesDaily'); // Legacy key for Subnautica daily games
    }
    if (game.id === 'aoe2-units') {
      keys.add('aoe2UnitsDaily'); // Legacy key for AOE2 daily games
    }
  });
  
  // Add generic patterns that tests expect (legacy from hardcoded approach)
  keys.add('customStreak'); // Generic streak key from tests
  keys.add('userDaily'); // Generic daily key from tests
  keys.add('gameCardDirectoryFilters'); // Generic CardDirectoryFilters key from tests
  
  return keys;
};

/**
 * Check if a localStorage key should be cleared based on game-related patterns
 * @param {string} key - The localStorage key to check
 * @param {Set<string>} gameKeys - The set of known game keys from SSoT
 * @returns {boolean} - True if the key should be cleared
 */
const isGameRelatedKey = (key, gameKeys) => {
  // First check if key is in the preserved list (never remove these)
  if (PRESERVED_KEYS.includes(key)) {
    return false;
  }
  
  // Check if key is in our dynamically generated game keys
  return gameKeys.has(key);
};

/**
 * Clear all localStorage statistics for games when user logs in
 * Uses dynamic keys from gameConfig SSoT for robust cleanup
 * Removes streak data AND daily game states since they'll now be stored in database
 */
export const clearLocalStorageStatistics = () => {
  try {
    const dynamicGameKeys = getDynamicGameKeys();
    let clearedCount = 0;
    
    // Since Object.keys(localStorage) isn't reliable in all environments,
    // we'll iterate through keys more directly
    const keysToCheck = [];
    
    // Try to get all keys - this works in most modern browsers
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          keysToCheck.push(key);
        }
      }
    } catch (error) {
      // Fallback to checking dynamically generated keys if enumeration fails
      console.warn('Could not enumerate localStorage keys, using dynamic keys fallback');
      keysToCheck.push(...Array.from(dynamicGameKeys));
    }
    
    // Process each key with individual error handling
    keysToCheck.forEach(key => {
      try {
        if (isGameRelatedKey(key, dynamicGameKeys)) {
          const existingValue = localStorage.getItem(key);
          if (existingValue) {
            console.log(`Removing localStorage key: ${key}`);
            localStorage.removeItem(key);
            clearedCount++;
          }
        }
      } catch (error) {
        // Log the specific error but continue processing other keys
        console.error(`Error removing localStorage key "${key}":`, error);
      }
    });
    
    console.log(`Successfully cleared ${clearedCount} game-related localStorage entries`);
    
  } catch (error) {
    console.error('Error during dynamic localStorage cleanup:', error);
    
    // Final fallback: use the dynamic keys directly
    console.log('Falling back to direct dynamic key cleanup...');
    try {
      const dynamicGameKeys = getDynamicGameKeys();
      let fallbackCount = 0;
      
      dynamicGameKeys.forEach(key => {
        try {
          const existingValue = localStorage.getItem(key);
          if (existingValue) {
            console.log(`Removing localStorage key (dynamic fallback): ${key}`);
            localStorage.removeItem(key);
            fallbackCount++;
          }
        } catch (keyError) {
          console.error(`Error removing localStorage key "${key}" during dynamic fallback:`, keyError);
        }
      });
      
      console.log(`Fallback cleared ${fallbackCount} entries`);
    } catch (fallbackError) {
      console.error('Dynamic fallback also failed:', fallbackError);
    }
  }
};
